import React from "react";

// "global" across all text inputs, used to allow only one required error per form
let isFirstRequiredErrorInForm: boolean;

type Result = {
  hasRequiredError: boolean;
  onInput(): void;
};

export const useFirstRequiredErrorInForm = <T extends HTMLElement>(ref: React.MutableRefObject<T | null>): Result => {
  const [hasRequiredError, setHasRequiredError] = React.useState(false);

  React.useEffect(() => {
    if (ref.current === null) {
      return;
    }

    // if this TextInput is in a form, handle "invalid" form events
    // these events can only occur when a user clicks on the form's submit button before
    // "touching" one or more TextInputs in that form

    if (ref.current && ref.current.closest("form")) {
      ref.current.addEventListener("invalid", requiredInputEmpty);

      return () => {
        ref.current?.removeEventListener("invalid", requiredInputEmpty);
      };
    }
  }, [ref.current]);

  const requiredInputEmpty = (event: Event): void => {
    event.preventDefault(); // dont show HTML5 required bubble

    // instead, show a walrus required error if this is the first invalid input in the form
    if (isFirstRequiredErrorInForm) {
      ref.current?.focus();
      setHasRequiredError(true);
      isFirstRequiredErrorInForm = false;
    }
  };

  // remove the required error onChange on the input value
  const onInput = (): void => {
    if (hasRequiredError) {
      setHasRequiredError(false);
    }
  };

  // (re)set global flag
  isFirstRequiredErrorInForm = true;

  return {
    hasRequiredError,
    onInput,
  };
};
